import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { Input } from '../ui/input';
import { cn } from 'lib/utils';
import { uiContainer, uiInput } from '../CutCorners';
import { InputPart, inputStates } from './input';
import BPButton from '../button/BPButton';
import { buttonStates } from '../button/button';
import BPIcon, { icons } from '../icons/BPIcon';
import { useKeyDown } from '@react-hooks-library/core';
import BPCurrencyIcon, { currencies } from '../currency/BPCurrencyIcon';

interface BPInputProps {
  state?: keyof typeof inputStates;
  twWidth?: string | null;
  twText?: string | null;
  placeholder?: string;
  disabled?: boolean;
  button?: {
    state?: keyof typeof buttonStates;
    onClick: (e?: MouseEvent<HTMLElement>, inputText?: string | null) => void;
    title: string;
  } | null;
  error?: string;
  focus?: boolean;
  type?: 'email' | 'password' | 'text';
  value?: string;
  isX?: boolean;
  icon?: keyof typeof icons | null;
  currency?: keyof typeof currencies | null;
  validate?: (text: string) => boolean;
}

const BPInput: FC<BPInputProps> = ({
  state = 'default',
  twWidth = null,
  twText = null,
  error = '',
  disabled = false,
  placeholder = '',
  focus = false,
  type = 'text',
  button = null,
  value = '',
  icon = null,
  isX = false,
  currency = null,
  validate = null,
}) => {
  const [success, setSuccess] = useState<boolean | null>(null);
  const [buttonState, setButtonState] =
    useState<keyof typeof inputStates>(state);
  const [inputValue, setInputValue] = useState<string>(value);

  useEffect(() => setButtonState(state), [state]);
  useEffect(() => setInputValue(value), [value]);

  useEffect(() => {
    if (value.length) {
      if (!validate) return;
      const passed = !!validate(value);
      setSuccess(passed);
      setButtonState(passed ? 'success' : 'error');
    }
  }, [value]);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focus && inputRef.current) inputRef.current.focus();
  }, [focus]);

  useKeyDown(['Enter'], () => {
    if (button && success) button.onClick(undefined, inputRef.current?.value);
  });

  return (
    <Input
      type={type}
      autoComplete="off"
      disabled={disabled}
      ref={inputRef}
      placeholder={placeholder}
      value={inputValue}
      onChange={(e) => {
        setInputValue(e.target.value);
        if (!validate) return;
        const passed = !!validate(e.target.value);
        setSuccess(passed);
        setButtonState(passed ? 'success' : 'error');
      }}
      className={cn(
        'group/input grid gap-4 items-center relative h-auto md:h-[72px] p-[10px] md:p-0 md:pr-[8px]',
        button
          ? 'grid-rows-[72px_auto] md:grid-cols-[1fr_171px]'
          : 'grid-cols-[1fr]',
        twWidth ?? 'w-full md:w-[644px]',
        twText ?? 'h-auto md:h-[72px] text-[18px]',
        icon || currency ? 'pl-[50px] md:pl-[50px]' : 'pl-[20px] md:pl-[20px]'
      )}
    >
      {isX && (
        <span className="absolute left-[14px] top-[32px] md:top-1/2 md:transform md:-translate-y-1/2 text-[18px] text-[rgba(255,255,255,0.5)] font-medium z-10">
          @
        </span>
      )}
      {icon && (
        <BPIcon
          icon={icon}
          className="block absolute left-[30px] z-10 fill-white"
        />
      )}
      {currency && (
        <BPCurrencyIcon
          currencyLogo={currency}
          container="circle"
          color
          className="absolute left-[20px] z-10"
        />
      )}
      {button && (
        <div className="z-10">
          <BPButton
            state={button.state}
            disabled={!success}
            onClick={(e) => button.onClick(e, inputRef.current?.value)}
          >
            {button.title}
          </BPButton>
        </div>
      )}
      <Background state={buttonState} withButton={!!button} />
      <Glow state={buttonState} withButton={!!button} />
      {error && <Error message={error} />}
    </Input>
  );
};

const Background: FC<{
  state: keyof typeof inputStates;
  withButton?: boolean;
}> = ({ state, withButton = false }) => {
  const base =
    'block absolute top-0 left-0 w-full h-full pointer-events-none z-0';

  const getPartClasses = (part: InputPart): string =>
    [part.default, part.hover, part.focus, part.disabled].join(' ');

  const bg = getPartClasses(inputStates[state].bg);
  const border = getPartClasses(inputStates[state].border);

  return (
    <>
      <div
        className={cn(
          base,
          bg,
          'backdrop-blur-md',
          withButton ? uiContainer.bg : uiInput.bg
        )}
      />
      <div
        className={cn(
          base,
          border,
          withButton ? uiContainer.border : uiInput.border
        )}
      />
    </>
  );
};

const Glow: FC<{ state: keyof typeof inputStates; withButton?: boolean }> = ({
  state,
  withButton = false,
}) => (
  <div
    className={cn(
      'grid absolute z-10 top-0 left-0 w-[200px] h-full items-center justify-start pointer-events-none peer-disabled/field:hidden',
      withButton ? uiContainer.bg : uiInput.bg
    )}
  >
    <div
      className={cn(
        'block absolute -left-[40px] w-[48px] h-[40px] blur-md transition-all duration-500 ease-in',
        inputStates[state].glow
      )}
    />
  </div>
);

const Error: FC<{ message: string }> = ({ message }) => (
  <div
    className={cn(
      'block absolute left-0 -bottom-[25px] text-[12px] font-semibold text-bionic-error pointer-events-none'
    )}
  >
    {message}
  </div>
);

export default BPInput;
